import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import * as Models from '../../models';
import { SOTProductTagViewModel } from '../../models';
import { SOTService } from '../../services';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import Utils from '../../../core/shared/utils';
import { BaseUnit } from '../../../core/shared/models';

export interface ProductSubDialogConfig {
  product: Models.SOTProductViewModel;
  extra: Models.SOTProductExtraItemViewModel;
  sot_service: SOTService;
  extragroup: Models.SOTProductExtraGroupViewModel;
  tempProduct: Models.SOTBasketProductViewModel;
}

export interface ProductSubDialogResult {
  status: "done" | "open_info_dialog",
  data: null | { product: Models.SOTProductViewModel, sub_product_label: string }
}

@Component({
  selector: "sot-productsubdialog",
  templateUrl: "./productsubdialog.component.html",
  styleUrls: ["./productsubdialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class ProductSubDialogComponent implements OnInit, OnDestroy {

  public isItemTagEnabled: boolean;
  public selectedTagFilter: number[];

  private _sotService: SOTService;

  public SOTProductTagViewModel: SOTProductTagViewModel[];
  public itemgroup: Models.SOTItemGroupViewModel = null;
  public product: Models.SOTProductViewModel;
  public extra: Models.SOTProductExtraItemViewModel;
  public extragroup: Models.SOTProductExtraGroupViewModel;
  public get tempProduct(): Models.SOTBasketProductViewModel {
    return this.data.tempProduct;
  }
  private _baseUnit = BaseUnit;
  /**baseUnit */
  public get baseUnit() {
    const me = this;
    return me._baseUnit;
  }

  public constructor(private _changeDetectorRef: ChangeDetectorRef
    , public dialogRef: MatDialogRef<ProductSubDialogComponent, ProductSubDialogResult>
    , @Inject(MAT_DIALOG_DATA) public data: ProductSubDialogConfig) {
    const me = this;
    me.product = data.product;
    me.extra = data.extra;
    me._sotService = data.sot_service;
    me.extragroup = data.extragroup;
  }

  public ngOnInit(): void {
    console.debug("--- sot-subproduct ngOnInit")
    const me = this;
    if (me.extra) {
      me._getItemTagSettings();
      me.selectedTagFilter = me._sotService.GetProductTagFilter();
      // Prefill Single Option Extragroups
      me.extra.product.extraGroups.forEach(extragroup => {
        if (extragroup.hasNoChoice && extragroup.extra_items.length > 0) {
          const extra = extragroup.extra_items[0];
          const subextra = (extra.without_subitem && !extra.with_one_subitem) ? null : extra.sub_items[0];
          me.setExtraCountTo(me.extragroup.max_choices, extragroup, extra, subextra);
        }
      });
      // Check and log missing products for extras 
      me.extra.product.extraGroups.forEach(extragroup => {
        extragroup.extra_items.forEach(extra => {
          if (!extra) {
            console.error(`Missing Product for Extra [ProductID=${extra.product_id}] "${extra.title}"`);
          }
        });
      });
    }
    me.PushChanges();
  }

  public ngOnDestroy(): void {
    console.debug("--- sot-subarticle ngOnDestroy")
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._sotService.coreLogService.error("ProductSubComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }
  public get isModeOne(): boolean {
    return this.data.sot_service.Accessabilty.useModeOne;
  }
  public get isModeTwo(): boolean {
    return this.data.sot_service.Accessabilty.useModeTwo;
  }

  public onDishClick(event: Event, extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    const me = this;
    event.stopPropagation();
    if (me.extragroup.max_choices === 1) {
      me.toggleExtraInSingleChoice(extragroup, extra, sub);
    } else {
      me.addExtra(extragroup, extra, sub);
    }
  }

  public isExtraSelected(extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    return this.getCount(extragroup, extra, sub) > 0;
  }

  /**
   * 
   * @param product sub extra
   * @param sub_product_label 
   */
  public showSubExtraProductInfo(product: Models.SOTProductViewModel, sub_product_label: string): void {
    const me = this;
    // dialog wird geschlossen, um den info dialog zu öffnen, danach wird der SubDialog neu geöffnet 
    me.dialogRef.close({ status: "open_info_dialog", data: { product: product, sub_product_label: sub_product_label } });
  }

  public Close(): void {
    this.dialogRef.close({ status: "done", data: null });
  }

  /** langkey, ob pfand incl. oder zzgl. */
  public get deposit_langkey(): string {
    return this._sotService.deposit_view ? "details_incl_deposit" : "details_plus_deposit";
  }

  public get displaySettingsImg() {
    return this._sotService.GetSettings().extra_as_image;
  }

  public hasDesription(description: string) {
    return !Utils.IsNullOrEmpty(description);
  }
  /** use e-commerce */
  public get use_eCommerce(): boolean {
    const me = this;
    return me._sotService.GetSettings().use_eCommerce;
  }

  public extraBaseUnit(extraItem: Models.SOTProductViewModel): BaseUnit {
    return extraItem.details.item_Base_Unit;
  }

  public extraPricePerUnit(extraItem: Models.SOTProductViewModel): number {
    const pricePerUnit = Utils.pricePerUnit(extraItem.priceWithExtras, extraItem.details.item_Base_Factor);
    return pricePerUnit;
  }

  /** Determines if the base price should be displayed */
  public showBasePrice(extraItem: Models.SOTProductViewModel): boolean {
    const baseUnit = extraItem.details.item_Base_Unit;
    // No base price if the unit is set to None
    if (baseUnit === BaseUnit.NONE) {
      return false;
    }
    const itemBaseFactor = extraItem.details.item_Base_Factor;
    // No base price if the unit is 1, e.g., 1 liter (no recalculation required by the customer)
    if (itemBaseFactor === 1) {
      return false;
    }
    const basePrice = extraItem.price;
    // No base price if the price is 0.00 €
    if (basePrice <= 0) {
      return false;
    }
    // Show base price if the itemBaseFactor is e.g. 250 gr., 0.75 l
    return true;
  }
  /** gets get item price for extra in the view */
  public itemViewPrice(): number {
    const me = this;
    if (me._sotService.isMenuConceptViewActive()) {
      // for menu concept, we need to reduce view price by min force price in group
      return me.extra.price - me.product.calcMenuMinForcePrice(me.extragroup);
    } else {
      return me.extra.price;
    }
  }
  private get hasTagSelected(): boolean {
    const me = this;
    return me._sotService.GetProductTagFilter().length > 0;
  }

  /** item tag is enabled */
  private async _getItemTagSettings() {
    const me = this;
    await me._sotService.getWebshopSettingsDataModel().then((webshopSettings) => {
      if (webshopSettings) {
        me.isItemTagEnabled = webshopSettings.is_itemtag_sot_enabled;
        me.PushChanges();
      }
    });
  }

  public GetExtraProductTagsByIds(tagIds: number[]) {
    const me = this;
    let extraTags: SOTProductTagViewModel[];
    if (me.hasTagSelected) {
      extraTags = me._sotService.GetProductTagsByIds(tagIds).sort((a, b) =>
        me._sotService.displaySelectedTagFirst(a.id, b.id)).splice(0, 5);
    } else {
      extraTags = me._sotService.GetProductTagsByIds(tagIds).splice(0, 5);
    }
    return extraTags;
  }

  public isResetTagFilterActive(extraId: number): boolean {
    const me = this;
    return me._sotService.isResetTagFilterActive(extraId);
  }

  public hasExtraSomeProductTags(product_tag_ids: number[]): boolean {
    const me = this;
    return me.selectedTagFilter.every((id => product_tag_ids.indexOf(id) >= 0));
  }

  public openResetExtraTagFilter(extra: Models.SOTProductExtraItemViewModel) {
    const me = this;
    if (me.isExtraAvailable(extra)) {
      me._sotService.openResetExtraTagFilter().then((result) => {
        if (result) {
          me._sotService.setResetTagFilterExtraIds(extra.id);
          me.PushChanges();
        }
      });
    }
  }

  public fadeOutExtraBySelectedTag(sub: Models.SOTProductExtraItemViewModel): boolean {
    const me = this;
    if (me.hasTagSelected) {
      let fadeOutExtra: boolean = false;
      if (sub.product.product_tag_ids.length > 0) {
        if (!me.isResetTagFilterActive(sub.id)) {
          fadeOutExtra = !me.hasExtraSomeProductTags(sub.product.product_tag_ids);
        }
      }
      return fadeOutExtra;
    }
  }

  private _findBasketExtraGroup(extragroup: Models.SOTProductExtraGroupViewModel): Models.SOTBasketItemExtraGroupViewModel {
    return this.tempProduct.extra_groups.find(eg => eg.query === extragroup.query);
  }

  /** For toggling Extras in an ExtraGroup with max Choice = 1 */
  private toggleExtraInSingleChoice(extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    const me = this;
    if (me.isExtraAvailable(extra, sub)) {
      if (me.getCount(extragroup, extra, sub) === 0) {
        me._removeAllExtras(extragroup);
        me.addExtra(extragroup, extra, sub);
      } else {
        me.removeExtra(extragroup, extra, sub);
      }
    }
    me.PushChanges();
  }

  public removeExtra(extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    const me = this;
    const canRemoveExtra = me.getCount(extragroup, extra, sub) > 0;
    if (canRemoveExtra) {
      me.tempProduct.removeExtraGroup(extragroup.query, extra.id, sub ? sub.id : 0);
    }
  }

  public addExtra(extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    const me = this;
    if (me._canAddExtra(extragroup, extra, sub)) {
      me.tempProduct.addExtraGroup(extragroup.query, extra.id, sub ? sub.id : 0);
    }
  }

  private _removeAllExtras(extragroup: Models.SOTProductExtraGroupViewModel) {
    const me = this;
    const basketExtraGroup = me._findBasketExtraGroup(extragroup);
    if (basketExtraGroup) {
      basketExtraGroup.extras.forEach((basketExtra) => {
        basketExtra.sub_extras.forEach((basketSubExtra) => {
          me.tempProduct.removeExtraGroup(extragroup.query, basketExtra.extra_id, basketSubExtra.extra_id);
        });
        me.tempProduct.removeExtraGroup(extragroup.query, basketExtra.extra_id);
      });
    }
  }

  public getCount(extragroup: Models.SOTProductExtraGroupViewModel, extra?: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    const existingExtraGroup = this._findBasketExtraGroup(extragroup);
    if (extra) {
      const existingExtra = existingExtraGroup ? existingExtraGroup.extras.find(e => e.extra_id === extra.id) : null;
      if (sub) {
        const existingSub = existingExtra ? existingExtra.sub_extras.find(e => e.extra_id === sub.id) : null;
        return existingSub ? existingSub.count : 0;
      }
      return existingExtra ? existingExtra.count : 0;
    }
    return existingExtraGroup ? existingExtraGroup.extras.reduce((a, b) => a + b.count, 0) : 0;
  }

  public setExtraCountTo(count: number, extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    const me = this;
    while (me.getCount(extragroup, extra, sub) > count) {
      me.removeExtra(extragroup, extra, sub);
      me.PushChanges();
    }
    while (me.getCount(extragroup, extra, sub) < count && me._canAddExtra(extragroup, extra, sub)) {
      me.addExtra(extragroup, extra, sub);
      me.PushChanges();
    }
  }

  private _canAddExtra(extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    const me = this;
    const lessThanMaximumAmountOfExtra = me.getCount(extragroup, extra) < me.extragroup.max_choices;
    const isAvailable = me.isExtraAvailable(extra, sub);
    return lessThanMaximumAmountOfExtra && isAvailable;
  }

  /** display the "i" button if the required settings are active and additional information is included
   * @param subProduct 
   * @returns hasAdditionalInfo
   */
  public displayAdditionalInformation(subProduct: Models.SOTProductViewModel): boolean {
    const me = this;
    let hasAdditionalInfo: boolean = false;
    const settings = me._sotService.GetSettings();
    if (settings && subProduct) {
      hasAdditionalInfo = ((settings.use_co2 && subProduct.details.co2 > 0) ||
        (settings.use_nutrition_values && subProduct.details.nutritions.nutritionItemViewModels.length > 0) ||
        (settings.use_allergens && subProduct.details.allergens.length > 0) ||
        (settings.use_additives && subProduct.details.additives.length > 0) ||
        (settings.use_eCommerce && (subProduct.details.item_Content.length > 0 || subProduct.details.hasProvider_Info))
      )
    }
    return hasAdditionalInfo;
  }

  public isExtraAvailable(extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel): boolean {
    return extra.product && extra.product.is_available && (sub ? sub.product && sub.product.is_available : true);
  }

  public isSubExtraAvailable(sub: Models.SOTProductExtraItemViewModel) {
    return sub.product && sub.product.is_available;
  }

  public choicesInExtra() {
    return (this.extragroup.max_choices - this.getCount(this.extragroup));
  }
}
